import type { MeDto } from "@clinic/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "./api";

export const meKey = ["me"] as const;

/** The signed-in user, or null when signed out. Never throws for "not signed in". */
export function useMe() {
  return useQuery({
    queryKey: meKey,
    queryFn: async (): Promise<MeDto | null> => {
      try {
        return await api<MeDto>("GET", "/auth/me");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

/** Wraps an auth call so its result becomes the cached session. */
export function useSessionMutation<I>(call: (input: I) => Promise<MeDto>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: call,
    onSuccess: (me) => {
      // Switching user or clinic invalidates every tenant-scoped cache entry.
      qc.clear();
      qc.setQueryData(meKey, me);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("POST", "/auth/logout"),
    onSettled: () => {
      qc.clear();
      qc.setQueryData(meKey, null);
    },
  });
}

export function activeMembership(me: MeDto) {
  return me.memberships.find((m) => m.clinicId === me.activeClinicId) ?? null;
}

export const canManage = (me: MeDto) => {
  const role = activeMembership(me)?.role;
  return role === "owner" || role === "admin";
};
